import { Inject, Injectable, InjectionToken, PLATFORM_ID } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { of } from 'rxjs';
import * as authActions from '../actions/customer.action';
import { switchMap, map, catchError, tap, take, withLatestFrom, filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { getRouterState, Go } from '../../../store/index';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AddressesService, CartsService, CustomerService } from 'src/app/shop/services';
import { getCustomerAccessToken } from '../selectors';
import { isPlatformServer } from '@angular/common';

import * as CartActions from '../actions/carts.action';
import * as klaviyoActions from '../actions/klaviyo.action';
import * as addressActions from '../actions/addresses.action';
import * as exemptionActions from '../actions/tax-exemptions.action';
import { getActiveLanguageNoCulture } from '@teamfoster/sdk/language-ngrx';
import { getCookiePreferences } from '@teamfoster/sdk/cookie-ngrx';
import { Analyzify } from 'src/app/utility/analyzify.utility';

@Injectable()
export class CustomerEffects {
  private STORAGE_KEY = 'CUSTOMER_TOKEN';

  constructor(
    @Inject(PLATFORM_ID) private platformId: InjectionToken<Object>,
    private actions$: Actions,
    private customerService: CustomerService,
    private addressesService: AddressesService,
    private cartService: CartsService,
    private store: Store,
    private router: Router,
    private sb: MatSnackBar
  ) {}

  GetCustomer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.LoadCustomer),
      withLatestFrom(this.store.select(getCustomerAccessToken), this.store.select(getActiveLanguageNoCulture)),
      switchMap(([action, token, lang]) =>
        this.customerService.GetCustomer(token?.accessToken ?? '', lang).pipe(
          map(customer => authActions.LoadCustomerSuccess({ customer })),
          catchError(errors => of(authActions.LoadCustomerFail({ errors })))
        )
      )
    );
  });

  CreateCustomer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.CreateCustomer),
      withLatestFrom(this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ data }, lang]) =>
        this.customerService.CreateCustomer(data, lang).pipe(
          map(result => authActions.CreateCustomerSuccess({ customer: { ...result.customerCreate?.customer, password: data.password } })),
          catchError(errors => of(authActions.CreateCustomerFail({ errors })))
        )
      )
    );
  });

  CreateCustomerSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.CreateCustomerSuccess),
      tap(() => {
        const cookies = this.store.selectSignal(getCookiePreferences);
        if (cookies()?.marketing) {
          Analyzify.SignupStart();
        }
      }),
      map(({ customer }) => authActions.CreateCustomerAccessToken({ data: { password: customer.password, email: customer.email } }))
    );
  });

  CreateCustomerAccessToken$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.CreateCustomerAccessToken),
      withLatestFrom(this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ data }, lang]) =>
        this.customerService.CustomizedCreateCustomerAccessToken(data, lang).pipe(
          map(token => authActions.CreateCustomerAccessTokenSuccess({ token, email: data.email })),
          catchError(errors => of(authActions.CreateCustomerAccessTokenFail({ errors })))
        )
      )
    );
  });

  SendInvitation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.SendInvitation),
      switchMap(({ email }) =>
        this.customerService.SendInvitation(email).pipe(
          map(success => authActions.SendInvitationSuccess({ success: success.success })),
          catchError(errors => of(authActions.SendInvitationFail({ errors })))
        )
      )
    );
  });

  // sign in
  CreateCustomerAccessTokenSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.CreateCustomerAccessTokenSuccess),
      tap(({ token }) => {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(token));

        const cookies = this.store.selectSignal(getCookiePreferences);
        if (cookies()?.marketing) {
          Analyzify.LoginStart();
        }
      }),
      switchMap(() =>
        this.store.select(getRouterState).pipe(
          take(1),
          filter(
            ({ state }) => !(state.queryParams['checkoutAfterSignin'] && JSON.parse(state.queryParams['checkoutAfterSignin']) === true)
          ),
          map(({ state }) => {
            return Go({
              path: [state.queryParams['returnUrl'] || '/account'],
            });
          })
        )
      )
    );
  });

  CreateCustomerAccessTokenSuccess2$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.CreateCustomerAccessTokenSuccess),
      map(a => authActions.LoadCustomer())
    );
  });

  LinkBasketToCustomerOnSignIn$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.CreateCustomerAccessTokenSuccess),
      map(({ token, email }) => CartActions.UpdateCartBuyerIdentity({ token: token.accessToken, email }))
    );
  });

  InitCustomerAccessToken$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.InitCustomerAccessToken),
      map(() => {
        if (isPlatformServer(this.platformId)) {
          return authActions.InitCustomerAccessTokenSuccess({});
        }
        const storedToken = localStorage.getItem(this.STORAGE_KEY);
        if (storedToken) {
          let token = JSON.parse(storedToken);
          if (new Date(token.expiresAt).getTime() < new Date().getTime()) {
            token = undefined;
            localStorage.removeItem(this.STORAGE_KEY);
          }
          return authActions.InitCustomerAccessTokenSuccess({ token });
        }
        return authActions.InitCustomerAccessTokenSuccess({});
      }),
      catchError(errors => of(authActions.InitCustomerAccessTokenFail({ errors })))
    );
  });

  DeleteCustomerAccessToken$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.DeleteCustomerAccessToken),
      withLatestFrom(this.store.select(getCustomerAccessToken), this.store.select(getActiveLanguageNoCulture)),
      switchMap(([action, token, lang]) =>
        this.customerService.DeleteCustomerAccessToken(token?.accessToken ?? '', lang).pipe(
          map(token => authActions.DeleteCustomerAccessTokenSuccess()),
          catchError(errors => of(authActions.DeleteCustomerAccessTokenFail({ errors })))
        )
      )
    );
  });

  ClearCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.DeleteCustomerAccessTokenSuccess),
      map(() => CartActions.ClearCart())
    );
  });

  RemoveKlaviyoState$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.DeleteCustomerAccessTokenSuccess),
      map(() => klaviyoActions.ResetKlaviyoState())
    );
  });

  ResetKlaviyoState$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.CreateCustomerAccessTokenSuccess),
      map(() => klaviyoActions.ResetKlaviyoState())
    );
  });

  DeleteAddresses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.DeleteCustomerAccessTokenSuccess),
      map(() => addressActions.ResetAddresses())
    );
  });

  DeleteCustomerAccessTokenSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.DeleteCustomerAccessTokenSuccess),
      tap(() => {
        localStorage.removeItem(this.STORAGE_KEY);
      }),
      switchMap(() =>
        this.store
          .select(getRouterState)
          .pipe(take(1))
          .pipe(
            map(({ state }) => {
              return Go({ path: [state.queryParams['returnUrl'] || '/'] });
            })
          )
      )
    );
  });

  RecoverCustomer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.RecoverCustomer),
      withLatestFrom(this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ email }, lang]) =>
        this.customerService.RecoverCustomer(email, lang).pipe(
          map(result => authActions.RecoverCustomerSuccess({ email: result?.email })),
          catchError(errors => of(authActions.RecoverCustomerFail({ errors })))
        )
      )
    );
  });

  ResetCustomer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.ResetCustomer),
      withLatestFrom(this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ resetCustomerData }, lang]) =>
        this.customerService.ResetCustomer(resetCustomerData, lang).pipe(
          map(result => authActions.ResetCustomerSucces()),
          catchError(errors => of(authActions.ResetCustomerFail({ errors })))
        )
      )
    );
  });

  ActivateCustomer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.ActivateCustomer),
      withLatestFrom(this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ activateCustomerData }, lang]) =>
        this.customerService.ActivateCustomer(activateCustomerData, lang).pipe(
          map(result => authActions.ActivateCustomerSuccess({ result })),
          catchError(errors => of(authActions.ActivateCustomerFail({ errors })))
        )
      )
    );
  });

  updateAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.UpdateCustomer),
      withLatestFrom(this.store.select(getCustomerAccessToken), this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ customer }, token, lang]) =>
        this.customerService.UpdateCustomer(token?.accessToken || '', customer, lang).pipe(
          map(customer => authActions.UpdateCustomerSuccess({ customer: customer })),
          catchError(errors => of(authActions.UpdateCustomerFail({ errors })))
        )
      )
    );
  });

  updateAccountSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.UpdateCustomerSuccess),
      switchMap(() =>
        this.store
          .select(getRouterState)
          .pipe(take(1))
          .pipe(
            map(({ state }) => {
              return Go({ path: [state.queryParams['returnUrl'] || state.url] });
            })
          )
      )
    );
  });

  updateAccountSuccess2$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.UpdateCustomerSuccess),
      map(a => authActions.LoadCustomer())
    );
  });

  updateAccountSuccess3$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.UpdateCustomerSuccess),
      map(a => exemptionActions.ResetTaxExemptionsCheck())
    );
  });

  SetDefaultAddress$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.SetDefaultAddress),
      withLatestFrom(this.store.select(getCustomerAccessToken), this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ id }, token, lang]) => {
        return this.addressesService.customerDefaultAddressUpdate(token?.accessToken || '', id, lang).pipe(
          map(address => authActions.SetDefaultAddressSuccess({ address })),
          catchError(errors => of(authActions.SetDefaultAddressFail({ errors })))
        );
      })
    );
  });

  updateCartAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(authActions.UpdateCustomerSuccess, authActions.SetDefaultAddress),
      withLatestFrom(this.store.select(getCustomerAccessToken)),
      map(([, token]) => CartActions.UpdateCartBuyerIdentity({ token: token?.accessToken ?? '' }))
    );
  });
}
